import { CdkConnectedOverlay, CdkOverlayOrigin, CdkScrollable } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { TellentNavigationItemBadge } from '@core/tellent-types';
import { BadgeComponent } from '@core/user-interface-legacy';

@Component({
  templateUrl: './navigation-label-badges.component.html',
  styleUrl: './navigation-label-badges.component.less',
  imports: [BadgeComponent, CdkConnectedOverlay, CdkOverlayOrigin],
  selector: `tlc-navigation-label-badges`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.minimized]': 'minimized()',
  },
})
export class NavigationLabelBadgesComponent {
  #elRef = inject(ElementRef);
  #cdkScroll = inject(CdkScrollable);
  #scroll = toSignal(this.#cdkScroll.elementScrolled().pipe(takeUntilDestroyed()));

  public visible = signal<boolean>(this.isVisible());
  public minimized = input<boolean>(false);
  public badges = input<TellentNavigationItemBadge[]>([]);

  public safeBadges = computed(() => {
    const badges = this.badges();
    const minimized = this.minimized();

    const _badges = minimized ? badges.slice(0, 1) : badges;

    return _badges.map(badge => {
      const maybeNumber = +badge.text;
      const isNumberBadge = Number.isInteger(maybeNumber);

      if (isNumberBadge) {
        if (minimized) {
          return {
            ...badge,
            text: maybeNumber > 9 ? '9+' : `${maybeNumber}`,
          };
        }

        return {
          ...badge,
          text: maybeNumber > 99 ? '99+' : `${maybeNumber}`,
        };
      }

      return badge;
    });
  });

  constructor() {
    effect(() => {
      this.#scroll();
      this.minimized() ? this.visible.set(this.isVisible()) : this.visible.set(false);
    });
  }

  public isVisible() {
    const scrollRect = this.#cdkScroll.getElementRef().nativeElement.getBoundingClientRect();
    const originRect = this.#elRef.nativeElement.getBoundingClientRect();

    return originRect.top >= scrollRect.top && originRect.bottom <= scrollRect.bottom;
  }
}
